import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUserTie } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import { useRole } from '../../context/roleContext';
import { USER_ROLES } from '../../utils/constants';
import { extractApiError } from '../../utils/apiError';
import PortalAccessCard from './PortalAccessCard';

/**
 * INITIAL_FORM
 *
 * Default shape used to initialize the customer form state.
 */
const INITIAL_FORM = {
  ownerName: '',
  companyName: '',
  gstNumber: '',
  contactPerson: '',
  customerType: '',
  address1: '',
  address2: '',
  state: '',
  city: '',
  country: '',
  zipCode: '',
  deliveryLocation: '',
  email: '',
  phoneNumber1: '',
  phoneNumber2: '',
  tallyName: '',
  creditPeriod: '',
  isActive: true,
  isAuthorized: true,
  sisterCompany: false,
};

/**
 * REQUIRED_FIELDS
 *
 * Fields that must be present for a valid form submission.
 */
const REQUIRED_FIELDS = [
  // ownerName, contactPerson and email are deliberately absent: a customer is
  // identified by companyName / tallyName, and those three are often unknown
  // when the record is first created. Requiring them only produced
  // placeholder values like "-", which look like real data.
  'companyName',
  'gstNumber',
  'customerType',
  'address1',
  'state',
  'city',
  'country',
  'zipCode',
  'phoneNumber1',
  'tallyName',
];

/**
 * CustomerForm
 *
 * A modal form used to create or edit a customer. Performs client-side
 * validation for required fields and basic format checks (email, phone).
 *
 * Props:
 * - customer: optional existing customer to edit
 * - isOpen: whether the modal is visible
 * - onClose: called when the user cancels
 * - onSubmit: async callback invoked with form values; expects { success }
 * - loading: disables inputs and shows loading state on submit
 */
const CustomerForm = ({
  customer = null,
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const { t } = useTranslation();
  const { userRole } = useRole();
  const [formValues, setFormValues] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  // Local submit state so the form owns its own button lifecycle (never stuck by
  // the parent's shared loading) and surfaces failures instead of silently doing
  // nothing. Always reset in finally.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const strings = t('customers.forms', { returnObjects: true });
  const isEditMode = Boolean(customer?.id);

  useEffect(() => {
    if (customer) {
      setFormValues({
        ...INITIAL_FORM,
        ...customer,
        sisterCompany: customer.sister_company || false,
      });
    } else {
      setFormValues(INITIAL_FORM);
    }
    setErrors({});
    setSubmitError('');
    setSubmitting(false);
  }, [customer, isOpen]);

  /**
   * handleChange(event)
   *
   * Generic input change handler that supports checkbox and text inputs.
   * Updates the `formValues` state for the given field name.
   */
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  /**
   * validate
   *
   * Memoized validators for common field formats (email, phone). Each
   * validator returns null when the value is valid or an error message.
   */
  const validate = useMemo(
    () => ({
      email: (value) => {
        if (!value) return null;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? null : strings.fields.email.errors.invalid;
      },
      phone: (value, field) => {
        if (!value) return null;
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(value)) {
          return strings.fields[field].errors?.invalid || strings.fields.phoneNumber1.errors.invalid;
        }
        return null;
      },
    }),
    [strings.fields]
  );

  /**
   * runValidation()
   *
   * Executes required-field checks and format validators. Updates the
   * `errors` state with messages for invalid fields and returns a boolean
   * indicating whether the form is valid.
   */
  const runValidation = () => {
    const nextErrors = {};

    REQUIRED_FIELDS.forEach((field) => {
      // Logic for conditional GST requirement
      if (field === 'gstNumber' && (!formValues.isAuthorized || formValues.sisterCompany)) {
        return;
      }

      if (!formValues[field]) {
        nextErrors[field] = strings.fields[field]?.errors?.required;
      }
    });

    const emailError = validate.email(formValues.email);
    if (emailError) nextErrors.email = emailError;

    const primaryPhoneError = validate.phone(formValues.phoneNumber1, 'phoneNumber1');
    if (primaryPhoneError) nextErrors.phoneNumber1 = primaryPhoneError;

    const secondaryPhoneError = validate.phone(formValues.phoneNumber2, 'phoneNumber2');
    if (secondaryPhoneError) nextErrors.phoneNumber2 = secondaryPhoneError;

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  /**
   * handleSubmit(event)
   *
   * Prevents default submit, runs validation and calls the async `onSubmit`
   * prop. On successful submission the form is reset.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;            // guard double-submit
    if (!runValidation()) return;

    setSubmitError('');
    setSubmitting(true);
    try {
      const result = await onSubmit?.(formValues);
      if (result && result.success) {
        setFormValues(INITIAL_FORM);
        // Parent closes the modal on success.
      } else {
        const rawErr = result && result.error;
        const msg = typeof rawErr === 'string' ? rawErr : (rawErr ? extractApiError(rawErr) : '');
        setSubmitError(msg || (strings.errors?.submitFailed) || 'Could not save. Please try again.');
      }
    } catch (err) {
      setSubmitError(extractApiError(err) || (strings.errors?.submitFailed) || 'Could not save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  /**
   * renderInput(name, type, props)
   *
   * Helper that renders a labeled input or textarea for the form using the
   * `strings` configuration and current `formValues` / `errors` state.
   */
  const renderInput = (name, type = 'text', props = {}) => {
    const fieldConfig = strings.fields[name];
    const hasError = Boolean(errors[name]);
    const { rows, ...restProps } = props;

    return (
      <div key={name} className="flex flex-col gap-2">
        <label className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }} htmlFor={name}>
          {fieldConfig?.label}
          {REQUIRED_FIELDS.includes(name) && (name !== 'gstNumber' || (formValues.isAuthorized && !formValues.sisterCompany)) && (
            <span className="ml-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
              {t('common.form.requiredIndicator')}
            </span>
          )}
        </label>
        {type === 'textarea' ? (
          <textarea
            id={name}
            name={name}
            value={formValues[name]}
            onChange={handleChange}
            placeholder={fieldConfig?.placeholder}
            rows={rows || 3}
            className="w-full rounded-xl border px-4 py-3 text-sm"
            style={{
              backgroundColor: UI_COLORS.FORMS.INPUT.background,
              borderColor: hasError ? UI_COLORS.BORDERS.ERROR : UI_COLORS.FORMS.INPUT.border,
              color: UI_COLORS.FORMS.INPUT.text,
            }}
            {...restProps}
          />
        ) : (
          <input
            id={name}
            name={name}
            type={type}
            value={formValues[name]}
            onChange={handleChange}
            placeholder={fieldConfig?.placeholder}
            className="w-full rounded-xl border px-4 py-3 text-sm"
            style={{
              backgroundColor: UI_COLORS.FORMS.INPUT.background,
              borderColor: hasError ? UI_COLORS.BORDERS.ERROR : UI_COLORS.FORMS.INPUT.border,
              color: UI_COLORS.FORMS.INPUT.text,
            }}
            {...restProps}
          />
        )}
        {hasError && (
          <p className="text-xs" style={{ color: UI_COLORS.STATUS.ERROR }}>
            {errors[name]}
          </p>
        )}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div
          className="flex items-center justify-between border-b px-6 py-5"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {isEditMode ? strings.title.edit : strings.title.add}
            </p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {isEditMode ? strings.subtitle.edit : strings.subtitle.add}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto px-6 py-6">
          <div className="grid gap-6 md:grid-cols-2">
            {renderInput('ownerName')}
            {renderInput('companyName')}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 mb-1">
                <input
                  type="checkbox"
                  id="isAuthorized"
                  name="isAuthorized"
                  checked={formValues.isAuthorized}
                  onChange={handleChange}
                  className="h-4 w-4 rounded"
                  style={{ accentColor: UI_COLORS.BUTTONS.PRIMARY.background }}
                />
                <label htmlFor="isAuthorized" className="text-xs font-medium cursor-pointer" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  Authorized Personal
                </label>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <input
                  type="checkbox"
                  id="sisterCompany"
                  name="sisterCompany"
                  checked={formValues.sisterCompany}
                  onChange={handleChange}
                  className="h-4 w-4 rounded"
                  style={{ accentColor: UI_COLORS.BUTTONS.PRIMARY.background }}
                />
                <label htmlFor="sisterCompany" className="text-xs font-medium cursor-pointer" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {strings.fields.sisterCompany?.label}
                </label>
              </div>
              {renderInput('gstNumber')}
            </div>
            {renderInput('contactPerson')}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }} htmlFor="customerType">
                {strings.fields.customerType.label}
                <span className="ml-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {t('common.form.requiredIndicator')}
                </span>
              </label>
              <select
                id="customerType"
                name="customerType"
                value={formValues.customerType}
                onChange={handleChange}
                className="w-full rounded-xl border px-4 py-3 text-sm"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.customerType ? UI_COLORS.BORDERS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                }}
              >
                <option value="">{strings.fields.customerType.placeholder}</option>
                <option value="Customer">{strings.fields.customerType.options.customer}</option>
                <option value="Supplier">{strings.fields.customerType.options.supplier}</option>
              </select>
              {errors.customerType && (
                <p className="text-xs" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {errors.customerType}
                </p>
              )}
            </div>
            {renderInput('address1', 'textarea', { rows: 3 })}
            {renderInput('address2', 'textarea', { rows: 3 })}
            {renderInput('state')}
            {renderInput('city')}
            {renderInput('country')}
            {renderInput('zipCode')}
            {renderInput('deliveryLocation', 'textarea', { rows: 3 })}
            {renderInput('email', 'email')}
            {renderInput('phoneNumber1')}
            {renderInput('phoneNumber2')}
            {renderInput('tallyName')}
            {renderInput('creditPeriod', 'number', { min: 0, step: 1 })}
          </div>

          <div className="mt-6 flex items-center justify-between rounded-xl border px-4 py-4" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <div className="flex items-center gap-3">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full"
                style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.SUCCESS.background, 0.15), color: UI_COLORS.BUTTONS.SUCCESS.background }}
              >
                <FontAwesomeIcon icon={faUserTie} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                  {strings.fields.isActive.label}
                </p>
                <p className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {formValues.isActive ? t('customers.statuses.active') : t('customers.statuses.inactive')}
                </p>
              </div>
            </div>
            <label className="inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formValues.isActive}
                onChange={handleChange}
                className="mr-3 h-6 w-6 rounded focus:ring-2"
                style={{ accentColor: UI_COLORS.BUTTONS.PRIMARY.background }}
              />
            </label>
          </div>

          {isEditMode && userRole === USER_ROLES.SUPER_ADMIN && customer?.id && (
            <div className="mb-6">
              <PortalAccessCard
                customerId={customer.id}
                defaultEmail={customer.email || formValues.email}
                defaultMobile={customer.phone_number_1 || formValues.phone_number_1 || formValues.phoneNumber1}
              />
            </div>
          )}

          {submitError && (
            <div
              className="rounded-xl px-4 py-3 text-sm font-medium"
              role="alert"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08),
                color: UI_COLORS.STATUS.ERROR,
                border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.3)}`,
              }}
            >
              {submitError}
            </div>
          )}

          {/* Buttons key off the form's own `submitting` (always reset in
              finally) so they can't be left stuck by the parent's shared loading. */}
          <div className="flex items-center justify-end gap-3 border-t pt-6" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105"
              disabled={submitting}
              style={{
                backgroundColor: 'transparent',
                color: UI_COLORS.TEXT.MUTED,
                border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
              }}
            >
              {strings.buttons.cancel}
            </button>
            <button
              type="submit"
              className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-70"
              disabled={submitting}
              style={{
                backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                color: UI_COLORS.BUTTONS.PRIMARY.text,
              }}
            >
              {submitting
                ? isEditMode
                  ? strings.buttons.submit.loading.edit
                  : strings.buttons.submit.loading.add
                : isEditMode
                  ? strings.buttons.submit.edit
                  : strings.buttons.submit.add}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerForm;
